import { Seo } from "../components/Seo";
import { PageHero, CtaLink } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { QuoteForm } from "../components/QuoteForm";
import { SuburbLinks } from "../components/SuburbLinks";
import { FaqSection } from "../components/FaqSection";
import { FinalCta } from "../components/FinalCta";
import { WORK_ITEMS, BUSINESS, FAQS } from "../content";
import { Check } from "lucide-react";
import { track } from "../lib/track";

const INCLUDED = [
  "Weekly, fortnightly and monthly regular cleans",
  "One-off and seasonal cleans",
  "Detailed deep cleaning",
  "Kitchens, bathrooms, living areas and bedrooms",
  "Tailored around your home and preferences",
  "The same familiar cleaners wherever possible",
];

export const ResidentialPage = () => (
  <>
    <Seo
      title="Regular House Cleaning Melbourne South East | Pure Bliss Clean"
      description="Looking for a regular house cleaner in Melbourne's South East? Weekly, fortnightly and monthly house cleaning across Carrum Downs, Frankston, Cranbourne, Langwarrin, Berwick, Mornington and surrounds. Get a free quote from Pure Bliss Clean."
      path="/residential-cleaning"
    />
    <PageHero
      eyebrow="Residential Cleaning"
      titleLines={["More Time For You.", "Less Time Cleaning."]}
      intro={`Keeping your home clean shouldn't take up your entire week. Pure Bliss Clean provides reliable regular house cleaning across ${BUSINESS.region}, designed to keep your home fresh, comfortable and cared for without the hassle.`}
      image="/assets/work/vanity-after.webp"
      imageAlt="Beautifully cleaned bathroom vanity in a Melbourne South East home"
    >
      <Reveal delay={0.4}>
        <div className="mt-8">
          <CtaLink to="#quote" testId="residential-page-cta" onClick={() => track("cta_click", { label: "residential_page" })}>Get a Free Quote</CtaLink>
        </div>
      </Reveal>
    </PageHero>

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>What's Included</Eyebrow>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Cleaning That Fits Your Home & Your Week</h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
            Recurring cleaning is at the heart of what we do. Most of our regular clients choose weekly or fortnightly
            cleans, with monthly arrangements available where suitable — plus one-off and deep cleans whenever your
            home needs extra attention.
          </p>
          <ul className="mt-8 space-y-3.5">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm font-semibold text-brand-ink">
                <span className="mt-0.5 rounded-full bg-teal-50 p-1 text-brand-teal"><Check className="h-3.5 w-3.5" /></span>
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <BeforeAfterSlider {...WORK_ITEMS.find((w) => w.id === "bath")} />
        </Reveal>
      </div>
    </section>

    <section className="bg-brand-mist">
      <div className="mx-auto max-w-3xl px-6 py-24 lg:py-32">
        <Reveal className="mb-10 text-center">
          <div className="flex justify-center"><Eyebrow>Free Quote</Eyebrow></div>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Tell Us About Your Home</h2>
          <p className="mx-auto mt-4 max-w-md text-slate-600">A few quick details and we'll be in touch to discuss your cleaning requirements.</p>
        </Reveal>
        <div id="quote">
          <QuoteForm defaultService="Regular House Cleaning" source="residential_page" />
        </div>
      </div>
    </section>

    <SuburbLinks />
    <FaqSection items={FAQS.filter((f) => ["Do you offer regular house cleaning?", "Can I book weekly or fortnightly cleans?", "Will I usually have the same cleaner?", "What happens if something is missed?"].includes(f.q))} heading="Common Questions" />
    <FinalCta />
  </>
);
